import { ROUTES } from '@/utils/routes';

/** Clerk redirect-origin allowlist for the single-host Platform deployment. */
export function buildClerkAllowedRedirectOrigins(): Array<string | RegExp> {
  const origins: Array<string | RegExp> = ['http://localhost:*'];

  if (typeof window === 'undefined') {
    return origins;
  }

  origins.push(window.location.origin);

  return [...new Set(origins)];
}

/** Clerk may put auth params in the query string or inside hash routing (#/?param=). */
export function readClerkAuthParamFromLocation(param: string, searchParams?: URLSearchParams): string | null {
  const fromPassedSearch = searchParams?.get(param);

  if (fromPassedSearch) {
    return fromPassedSearch;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  const fromWindowSearch = new URLSearchParams(window.location.search).get(param);

  if (fromWindowSearch) {
    return fromWindowSearch;
  }

  const hash = window.location.hash;

  if (!hash || hash.length <= 1) {
    return null;
  }

  const hashBody = hash.startsWith('#') ? hash.slice(1) : hash;
  const queryIndex = hashBody.indexOf('?');

  if (queryIndex === -1) {
    return null;
  }

  return new URLSearchParams(hashBody.slice(queryIndex + 1)).get(param);
}

const PUBLIC_AUTH_REDIRECT_PATHS = new Set([
  ROUTES.SIGN_IN,
  ROUTES.SIGN_UP,
  ROUTES.LANDING_1_SIGN_UP,
  ROUTES.SSO_SIGN_IN,
  ROUTES.FORGOT_PASSWORD,
  ROUTES.RESET_PASSWORD,
  ROUTES.VERIFY_EMAIL,
]);

const LOCALHOST_NAMES = new Set(['localhost', '127.0.0.1', '[::1]']);

function getCurrentOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
}

function normalizePathname(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }

  return pathname;
}

function isLocalhostUrl(url: URL): boolean {
  return url.protocol === 'http:' && LOCALHOST_NAMES.has(url.hostname);
}

function isPublicAuthRedirectPath(pathname: string): boolean {
  const normalizedPathname = normalizePathname(pathname);

  return Array.from(PUBLIC_AUTH_REDIRECT_PATHS).some(
    (publicPath) => normalizedPathname === publicPath || normalizedPathname.startsWith(`${publicPath}/`)
  );
}

export function isPublicAuthRedirectUrl(
  redirectUrl: string | null | undefined,
  currentOrigin = getCurrentOrigin()
): boolean {
  if (!redirectUrl) {
    return false;
  }

  try {
    const parsed = new URL(redirectUrl, currentOrigin);

    return isPublicAuthRedirectPath(parsed.pathname);
  } catch {
    return false;
  }
}

export function isSafeClerkRedirectUrl(
  redirectUrl: string | null | undefined,
  currentOrigin = getCurrentOrigin()
): redirectUrl is string {
  if (!redirectUrl || isPublicAuthRedirectUrl(redirectUrl, currentOrigin)) {
    return false;
  }

  try {
    const parsed = new URL(redirectUrl, currentOrigin);
    const origin = new URL(currentOrigin);

    return parsed.origin === origin.origin || isLocalhostUrl(parsed);
  } catch {
    return false;
  }
}

/** Clerk may put redirect_url in the query string or inside hash routing (#/?redirect_url=). */
export function readClerkRedirectUrlParam(searchParams?: URLSearchParams): string | null {
  return readClerkAuthParamFromLocation('redirect_url', searchParams);
}

export function readSafeClerkRedirectUrlParam(searchParams?: URLSearchParams): string | null {
  const redirectUrl = readClerkRedirectUrlParam(searchParams);

  return isSafeClerkRedirectUrl(redirectUrl) ? redirectUrl : null;
}
